import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import CommonInput from "../input/CommonInput";
import CommonButton from "../button/CommonButton";
import PhoneInput from "../input/PhoneInput";
import DocumentUpload from "../input/DocumentUpload";
import {
  emailValidator,
  passwordValidator,
  handleBlanckSpaceInput,
  handleUpperCaseNoSpace,
  getErrorMessage
} from "../../constants/globalFunction";
import { imagePath } from "../../constants/imagePath";
import { showErrorToast, showSuccessToast } from "../../utils/toast";
import { FaUser, FaEnvelope, FaLock } from "react-icons/fa";
import "./AuthModal.css";
import { appApiHandler } from "../../api/appApiHandler";
import {
  saveToken,
  saveUserId,
  saveUserType,
} from "../../constants/asyncStoreManager";
import { setUserData } from "../../store/slices/userState";
import { setIsLoggedIn } from "../../store/slices/authState";
import { string } from "../../constants/string";

const AuthModal = ({ visible, onClose }) => {
  const dispatch = useDispatch();
  const [isLogin, setIsLogin] = useState(true);
  const [userType, setUserType] = useState("retailer");

  // Login states
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginEmailError, setLoginEmailError] = useState("");
  const [loginPasswordError, setLoginPasswordError] = useState("");
  const [isSubmitting, setSubmitting] = useState(false);

  // Signup states
  const [name, setName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [phoneno, setPhoneno] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [country, setCountry] = useState('India');
  const [nameError, setNameError] = useState("");
  const [signupEmailError, setSignupEmailError] = useState("");
  const [signupPasswordError, setSignupPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [phonenoError, setPhonenoError] = useState("");
  const [loading, setLoading] = useState(false);

  const [gst, setGst] = useState(null);
  const [gstNo, setGstNo] = useState("");
  const [gstNoError, setGstNoError] = useState("");
  const [gstError, setGstError] = useState("");
  const [panCard, setPanCard] = useState(null);
  const [panCardNo, setPanCardNo] = useState("");
  const [panCardNoError, setPanCardNoError] = useState("");
  const [panCardError, setPanCardError] = useState("");
  const [passport, setPassport] = useState(null);
  const [passportNo, setPassportNo] = useState("");
  const [passportNoError, setPassportNoError] = useState("");
  const [passportError, setPassportError] = useState("");
  const [profileImage, setProfileImage] = useState(null);

  // Reset form when switching between login/signup
  useEffect(() => {
    if (visible) {
      resetForms();
    }
  }, [visible, isLogin]);

  const resetForms = () => {
    // Reset login form
    setLoginEmail("");
    setLoginPassword("");
    setLoginEmailError("");
    setLoginPasswordError("");
    setSubmitting(false);

    // Reset signup form
    setName("");
    setSignupEmail("");
    setSignupPassword("");
    setConfirmPassword("");
    setPhoneno("");
    setNameError("");
    setSignupEmailError("");
    setSignupPasswordError("");
    setConfirmPasswordError("");
    setPhonenoError("");
    setLoading(false);
    setGst(null);
    setGstNo("");
    setGstNoError("");
    setGstError("");
    setPanCard(null);
    setPanCardNo("");
    setPanCardNoError("");
    setPanCardError("");
    setPassport(null);
    setPassportNo("");
    setPassportNoError("");
    setPassportError("");
    setProfileImage(null);
  };

  // Login validation
  const validateLogin = () => {
    let ok = true;
    if (!loginEmail.trim()) {
      setLoginEmailError("Please enter email");
      ok = false;
    } else if (!emailValidator(loginEmail)) {
      setLoginEmailError("Please enter a valid email");
      ok = false;
    } else {
      setLoginEmailError("");
    }

    if (!loginPassword.trim()) {
      setLoginPasswordError("Please enter password");
      ok = false;
    } else if (!passwordValidator(loginPassword)) {
      setLoginPasswordError(
        "Password must be at least 8 characters and include one uppercase letter, one number, and one special character"
      );
      ok = false;
    } else {
      setLoginPasswordError("");
    }
    return ok;
  };

  // Signup validation
  const validateSignup = () => {
    let isValid = true;

    if (!name.trim()) {
      setNameError("Please enter name");
      isValid = false;
    } else {
      setNameError("");
    }

    if (!phoneno.trim()) {
      setPhonenoError("Please enter phone number");
      isValid = false;
    } else {
      setPhonenoError("");
    }

    if (countryCode === "+91") {
      const digitsOnlyLength = phoneno.trim().length;
      if (digitsOnlyLength !== 10) {
        setPhonenoError("Please enter valid phone number");
        isValid = false;
      }
    }

    if (!signupEmail.trim()) {
      setSignupEmailError("Please enter email");
      isValid = false;
    } else if (!emailValidator(signupEmail)) {
      setSignupEmailError("Please enter a valid email");
      isValid = false;
    } else {
      setSignupEmailError("");
    }

    if (!signupPassword.trim()) {
      setSignupPasswordError("Please enter password");
      isValid = false;
    } else if (!passwordValidator(signupPassword)) {
      setSignupPasswordError(
        "Password must be at least 8 characters and include one uppercase letter, one number, and one special character"
      );
      isValid = false;
    } else {
      setSignupPasswordError("");
    }

    if (!confirmPassword.trim()) {
      setConfirmPasswordError("Please enter confirm password");
      isValid = false;
    } else if (confirmPassword !== signupPassword) {
      setConfirmPasswordError("Passwords do not match");
      isValid = false;
    } else {
      setConfirmPasswordError("");
    }

    // Wholeseller specific validations for India
    if (userType === "wholeseller" && countryCode === "+91") {
      if (!gstNo.trim()) {
        setGstNoError("Please enter GST number");
        isValid = false;
      } else {
        setGstNoError("");
      }

      if (!gst) {
        setGstError("Please upload GST document");
        isValid = false;
      } else {
        setGstError("");
      }

      if (!panCardNo.trim()) {
        setPanCardNoError("Please enter PAN card number");
        isValid = false;
      } else {
        setPanCardNoError("");
      }

      if (!panCard) {
        setPanCardError("Please upload PAN card");
        isValid = false;
      } else {
        setPanCardError("");
      }
    }

    // Wholeseller specific validations for non-India
    if (userType === "wholeseller" && countryCode !== "+91") {
      if (!passportNo.trim()) {
        setPassportNoError("Please enter passport number");
        isValid = false;
      } else {
        setPassportNoError("");
      }

      if (!passport) {
        setPassportError("Please upload passport");
        isValid = false;
      } else {
        setPassportError("");
      }
    }

    return isValid;
  };

  // Login submit
  const onLoginSubmit = async (e) => {
    e.preventDefault();
    if (!validateLogin()) return;
    try {
      const reqData = {
        email: loginEmail.trim(),
        password: loginPassword.trim(),
      };
      setSubmitting(true);
      const response = await appApiHandler.login(reqData);

      if (response?.status === 200) {
        await saveUserId(response?.data?._id);
        await saveUserType(response?.data?.user_type);
        await saveToken(response?.token);
        dispatch(setUserData(response?.data));
        dispatch(setIsLoggedIn(true));
        showSuccessToast("Login successful!");
        onClose();
      } else if (response?.message) {
        showErrorToast(getErrorMessage(response?.message));
      } else {
        showErrorToast(string.error.failed_due_to_internal_error);
      }
    } catch (err) {
      console.log("Login Error", err);
      showErrorToast(string.error.failed_due_to_internal_error);
    } finally {
      setSubmitting(false);
    }
  };

  // Signup submit
  const onSignupSubmit = async (e) => {
    e.preventDefault();
    if (!validateSignup()) return;

    try {
      const formData = new FormData();
      formData.append("user_type", userType);
      formData.append("name", name.trim());
      formData.append("email", signupEmail.trim());
      formData.append("password", signupPassword.trim());
      formData.append("phoneno", phoneno.trim());
      formData.append("country_code", countryCode.trim());
      formData.append("country", country.trim());

      if (profileImage) {
        formData.append("profile_image", profileImage);
      }

      if (userType === "wholeseller" && countryCode === "+91") {
        if (gst) {
          formData.append("gst", gst);
        }
        formData.append("gst_no", gstNo.trim());

        if (panCard) {
          formData.append("pancard", panCard);
        }
        formData.append("pancard_no", panCardNo.trim());
      } else if (userType === "wholeseller" && countryCode !== "+91") {
        if (passport) {
          formData.append("passport", passport);
        }
        formData.append("passport_no", passportNo.trim());
      }

      setLoading(true);
      const response = await appApiHandler.register(formData);

      if (response?.status === 200) {
        await saveUserId(response?.data?._id);
        await saveUserType(response?.data?.user_type);
        await saveToken(response?.token);
        dispatch(setUserData(response?.data));
        dispatch(setIsLoggedIn(true));
        showSuccessToast("Account created successfully!");
        onClose();
      } else if (response?.message) {
        showErrorToast(getErrorMessage(response?.message));
      } else {
        showErrorToast(string.error.failed_due_to_internal_error);
      }
    } catch (err) {
      console.log("Signup Error", err);
      showErrorToast(string.error.failed_due_to_internal_error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (setFile, setError) => (file) => {
    setFile(file);
    setError("");
  };

  const onChangeCountryCode = (code) => {
    setCountryCode(code);
    if (code === "+91") {
      setCountry('India');
    } else {
      setCountry('');
    }
  };

  const onPressWholeseller = () => {
    setUserType("wholeseller");
  };

  const onPressLogin = () => {
    setIsLogin(true);
  };

  if (!visible) return null;

  return (
    <div className="auth-modal-overlay">
      <div className="auth-modal-container">
        <div className="auth-modal-inner">
          <button className="auth-modal-close" onClick={onClose}>
            ×
          </button>

          <div className="auth-modal-header">
            <img
              src={imagePath.appLogo}
              alt="Codeinfoway"
              className="auth-modal-logo"
            />
            <h2 className="auth-modal-title">
              {isLogin ? "Login" : "Create New Account"}
            </h2>
          </div>

          {isLogin ? (
            // Login Form
            <form onSubmit={onLoginSubmit} className="auth-modal-form">
              <CommonInput
                label="Email"
                value={loginEmail}
                onChange={setLoginEmail}
                placeholder="Enter email"
                error={loginEmailError}
                type="email"
                leftIcon={<FaEnvelope className="auth-modal-icon" />}
              />
              <CommonInput
                label="Password"
                value={loginPassword}
                onChange={(value) => setLoginPassword(handleBlanckSpaceInput(value))}
                placeholder="Enter password"
                error={loginPasswordError}
                secureTextEntry
                leftIcon={<FaLock className="auth-modal-icon" />}
              />
              <CommonButton
                title={isSubmitting ? "Logging in…" : "Log In"}
                type="submit"
                disabled={isSubmitting}
              />
              <div className="auth-modal-switch">
                Don't have an account?{" "}
                <span className="auth-modal-link" onClick={() => setIsLogin(false)}>
                  Sign Up
                </span>
              </div>
            </form>
          ) : (
            // Signup Form
            <form onSubmit={onSignupSubmit} className="auth-modal-form">
              {/* User Type Toggle */}
              <div className="auth-modal-toggle-container">
                <button
                  className={`auth-modal-toggle-button ${userType === "retailer"
                    ? "auth-modal-toggle-button-selected-left"
                    : ""
                    }`}
                  onClick={() => setUserType("retailer")}
                  type="button"
                >
                  <span
                    className={`auth-modal-toggle-button-text ${userType === "retailer"
                      ? "auth-modal-toggle-button-text-selected"
                      : ""
                      }`}
                  >
                    Retailer
                  </span>
                </button>
                <button
                  className={`auth-modal-toggle-button ${userType === "wholeseller"
                    ? "auth-modal-toggle-button-selected-right"
                    : ""
                    }`}
                  onClick={onPressWholeseller}
                  type="button"
                >
                  <span
                    className={`auth-modal-toggle-button-text ${userType === "wholeseller"
                      ? "auth-modal-toggle-button-text-selected"
                      : ""
                      }`}
                  >
                    Wholesaler
                  </span>
                </button>
              </div>

              <CommonInput
                label="Name"
                value={name}
                onChange={setName}
                placeholder="Enter name"
                error={nameError}
                leftIcon={<FaUser className="auth-modal-icon" />}
              />

              <CommonInput
                label="Email"
                value={signupEmail}
                onChange={setSignupEmail}
                placeholder="Enter email"
                error={signupEmailError}
                type="email"
                leftIcon={<FaEnvelope className="auth-modal-icon" />}
              />

              <PhoneInput
                label="Phone number"
                code={countryCode}
                onChangeCode={onChangeCountryCode}
                onChangeText={setPhoneno}
                value={phoneno}
                placeholder="Enter phone number"
                error={phonenoError}
                editable={!loading}
              />

              <CommonInput
                label="Password"
                value={signupPassword}
                onChange={(value) => setSignupPassword(handleBlanckSpaceInput(value))}
                placeholder="Enter password"
                error={signupPasswordError}
                secureTextEntry
                leftIcon={<FaLock className="auth-modal-icon" />}
              />

              <CommonInput
                label="Confirm Password"
                value={confirmPassword}
                onChange={(value) =>
                  setConfirmPassword(handleBlanckSpaceInput(value))
                }
                placeholder="Enter confirm password"
                error={confirmPasswordError}
                secureTextEntry
                leftIcon={<FaLock className="auth-modal-icon" />}
              />

              <DocumentUpload
                label="Profile Image"
                required={false}
                value={profileImage}
                onChange={(file) => {
                  setProfileImage(file);
                }}
                disabled={loading}
                placeholder="Select Profile Image"
              />

              {/* Wholeseller specific fields for India */}
              {userType === "wholeseller" && countryCode === "+91" && (
                <>
                  <CommonInput
                    label="GST Number"
                    value={gstNo}
                    onChange={(value) => setGstNo(handleUpperCaseNoSpace(value))}
                    placeholder="Enter GST number"
                    error={gstNoError}
                  />

                  <DocumentUpload
                    label="GST"
                    required={true}
                    value={gst}
                    onChange={handleFileUpload(setGst, setGstError)}
                    error={gstError}
                    disabled={loading}
                  />

                  <CommonInput
                    label="PAN Card Number"
                    value={panCardNo}
                    onChange={(value) =>
                      setPanCardNo(handleUpperCaseNoSpace(value))
                    }
                    placeholder="Enter PAN card number"
                    error={panCardNoError}
                  />

                  <DocumentUpload
                    label="PAN Card"
                    required={true}
                    value={panCard}
                    onChange={handleFileUpload(setPanCard, setPanCardError)}
                    error={panCardError}
                    disabled={loading}
                  />
                </>
              )}

              {/* Wholeseller specific fields for non-India */}
              {userType === "wholeseller" && countryCode !== "+91" && (
                <>
                  <CommonInput
                    label="Passport Number"
                    value={passportNo}
                    onChange={(value) =>
                      setPassportNo(handleUpperCaseNoSpace(value))
                    }
                    placeholder="Enter passport number"
                    error={passportNoError}
                  />

                  <DocumentUpload
                    label="Passport"
                    required={true}
                    value={passport}
                    onChange={handleFileUpload(setPassport, setPassportError)}
                    error={passportError}
                    disabled={loading}
                  />
                </>
              )}

              <CommonButton
                title={loading ? "Creating Account..." : "Sign Up"}
                type="submit"
                disabled={loading}
              />

              <div className="auth-modal-switch">
                Already have an account?{" "}
                <span className="auth-modal-link" onClick={onPressLogin}>
                  Log In
                </span>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
